import type { DragEvent } from "react";
import type { AttachmentSource } from "@/libs/form-attachment";

const ATTACHMENT_DRAG_MIME = "application/x-birdwatch-attachments";

export function setAttachmentsDragData(
  dataTransfer: DataTransfer,
  attachments: AttachmentSource[],
) {
  dataTransfer.setData(ATTACHMENT_DRAG_MIME, JSON.stringify(attachments));
  dataTransfer.effectAllowed = "copy";
}

// `getData` is unreadable during dragover — only the type list is exposed.
export function hasAttachmentsDragData(dataTransfer: DataTransfer | null) {
  return dataTransfer?.types.includes(ATTACHMENT_DRAG_MIME) ?? false;
}

export function getAttachmentsFromDragData(
  dataTransfer: DataTransfer | null,
): AttachmentSource[] {
  const raw = dataTransfer?.getData(ATTACHMENT_DRAG_MIME);
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** The cursor of an element spread with `getAttachmentsDragSourceProps`. */
export const ATTACHMENT_DRAG_SOURCE_CLASS_NAME =
  "cursor-grab active:cursor-grabbing";

/** Makes an element a drag source carrying `attachments`; the element itself is the drag ghost. */
export function getAttachmentsDragSourceProps(attachments: AttachmentSource[]) {
  return {
    draggable: true,
    onDragStart: (e: DragEvent<HTMLElement>) => {
      setAttachmentsDragData(e.dataTransfer, attachments);
      // The browser default drags the raw <img> at its intrinsic size, which looks distorted.
      const rect = e.currentTarget.getBoundingClientRect();
      e.dataTransfer.setDragImage(
        e.currentTarget,
        e.clientX - rect.left,
        e.clientY - rect.top,
      );
    },
  };
}

export function getAttachmentsDropZoneProps(
  onDropAttachments: (attachments: AttachmentSource[]) => void,
) {
  return {
    onDragOver: (e: DragEvent) => {
      if (hasAttachmentsDragData(e.dataTransfer)) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      }
    },
    onDrop: (e: DragEvent) => {
      const attachments = getAttachmentsFromDragData(e.dataTransfer);
      if (attachments.length > 0) {
        e.preventDefault();
        onDropAttachments(attachments);
      }
    },
  };
}
